/* outputs.js — WI-14 Reports & Drawings output builders (System A pricing ONLY).
 *
 * Ported from lhc-app.html:
 *   getWorktopRuns        ~31750  (worktop run merger + 45°-corner detection)
 *   lhcSizingNote         ~31906  (production note for custom-sized placements)
 *   lhcCustomDimLabel     ~31933  ("550w×600h" line-item label)
 *   quoteState defaults   ~47968  (multiplier/discounts/transport/assembly/VAT/qty)
 *   quoteSubtotal         ~48069  (Σ effDef.price × qty — NB: catalog price, NO
 *                                  custom-size surcharge here; the surcharge only
 *                                  appears in payload LINES. Prototype quirk — keep.)
 *   recomputeQuote        ~48078  (gross→D1→D2→+transport/assembly→VAT→final)
 *   buildOrderPayload     ~48103  (I-1 canonical order payload — single source that
 *                                  the Preventivo doc / Odoo / production pkg consume)
 *   buildPreventivoDoc    ~48211  (printable Preventivo HTML — VERBATIM template
 *                                  for Modules+Price; Iteration 21 appends the
 *                                  additive Worktops / End panels / Fillers /
 *                                  Technical validation sections after Price,
 *                                  each omitted when empty — a legacy payload
 *                                  still renders the legacy doc byte-identically)
 *   buildQuotePDF         ~39104  (quote HTML document — uses pricing.buildQuoteLines
 *                                  + the SAME totals math as pricing.quoteTotals)
 *   buildPrintHTML        ~41197  (A4 Quotation print doc for saved client quotes)
 *   renderReportsPage     ~20363  (overview KPIs / grouped modules / donut / finishes)
 *   repRenderOrderEntry   ~20553, repRenderVolume ~20598, repRenderSchedule ~20644,
 *   repRenderMaterials    ~20671, REP_SHIP_DENSITY ~20524, repProjectModules ~20526
 *   dwpUnitRows           ~54657  (drawings unit schedule rows)
 *
 * Pure: every function takes (state, lib, …) explicitly — state is the engine design
 * state ({ walls, placements, wallOpenings, kitchenWizard }), lib the MODULE_LIBRARY.
 * All pricing reads def.price / priceG1..G4 through pricing.js (SYSTEM A). System B
 * (pricingSystemB.js per-component build-up) is NEVER consulted here.
 */
import { formatMoney } from "../../lib/money";
import { OPENING_DEFAULTS } from "./openings";
import { DEF_OVERHANG_FRONT_MM } from "./surfaces";
import { LHC_RULES, DEFAULT_WALL_HEIGHT } from "./constants";
import { getPricing } from "./pricingSettings";
import { getUpperLineM } from "./rules";
import { getWallPlacementInfo, cabinetWorldPos } from "./placement";
import { getBackWall } from "./walls";
import { effDef, isCustomSized, buildQuoteLines } from "./pricing";
import { getWallGraph, jointTol } from "./wallGraph";
import { buildEndPanels } from "./endPanels";
import { buildFillerSchedule } from "./fillerSchedule";
import { evaluateRules } from "./ruleEngine";

/* ── Constants (verbatim) ─────────────────────────────────────────────────── */
export // 2026-08-18: imported from the surfaces oracle — was a drift-prone copy
const WORKTOP_OVERHANG_M = DEF_OVERHANG_FRONT_MM / 1000;   // ~31747 — counter slab front overhang
export const WORKTOP_CORNER_TOL_M = 0.05;  // ~31748 — wall-endpoint coincidence tol
export const REP_SHIP_DENSITY = 160;       // ~20524 — kg per m³ assembled cabinetry
export const QUOTE_VOLUME_FACTOR = 0.002;  // ~47966 — m³ per € of gross (demo heuristic)
export const QUOTE_WEIGHT_FACTOR = 0.045;  // ~47967 — kg per € of gross

/* ── Money formatters (each output keeps ITS OWN locale — verbatim) ───────── */
/** Reports pages: '€ 1.234,56' (de-DE, 2dp). (~20538 repFmtEUR) */
export function repFmtEUR(v) {
  return "€ " + (v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/** Quote modal / engine footer: '€ 1,234.56' (en-US). (~45960 fmtEUR) */
export function fmtEUR(n) {
  return formatMoney(n);   // ONE money formatter platform-wide (2026-08-18)
}
/** Reports overview KPI: '€1.235' rounded (de-DE). (~20367) */
export function repFmtEURRound(n) {
  return "€" + Math.round(n).toLocaleString("de-DE");
}

/* ── v0.30 sizing helpers (~31906/31933) ──────────────────────────────────── */
export function sizingNote(p, lib) {
  const cfg = p.config;
  if (!cfg || (cfg.customWidthMm == null && cfg.customHeightMm == null)) return null;
  const def = lib[p.type];
  const parts = [];
  if (cfg.customWidthMm != null) {
    const stdMm = def ? Math.round(def.width * 1000) : "?";
    const delta = cfg.customWidthMm - stdMm;
    const prefix = delta < 0 ? "RID. IN LAR." : "FUORI MISURA LAR.";
    parts.push(prefix + " mm " + Math.abs(delta) + " (std: " + stdMm + "mm → " + cfg.customWidthMm + "mm)");
  }
  if (cfg.customHeightMm != null) {
    const stdMm = def ? Math.round(def.height * 1000) : "?";
    const delta = cfg.customHeightMm - stdMm;
    const prefix = delta < 0 ? "RID. IN ALT." : "FUORI MISURA ALT.";
    parts.push(prefix + " mm " + Math.abs(delta) + " (std: " + stdMm + "mm → " + cfg.customHeightMm + "mm)");
  }
  return parts.join(" · ");
}

export function customDimLabel(p) {
  const cfg = p && p.config;
  if (!cfg) return "";
  const parts = [];
  if (cfg.customWidthMm != null) parts.push(cfg.customWidthMm + "w");
  if (cfg.customHeightMm != null) parts.push(cfg.customHeightMm + "h");
  return parts.join("×");
}

/* ── getWorktopRuns (~31750): merged counter runs + 45° corner cuts ───────── */
export function getWorktopRuns(state, lib) {
  const wls = (state && state.walls) || [];
  const pls = (state && state.placements) || [];
  if (!wls.length || !pls.length) return [];
  const backWall = getBackWall(wls); // ~31195: horizontal wall with the smallest mid-z

  const raw = []; // { wallId, start, end, depth }
  wls.forEach((wall) => {
    if (wall.style === "peninsula" || wall.type === "arc") return;
    const info = getWallPlacementInfo(wall, wls);
    if (!info) return;
    const segs = [];
    pls.forEach((p) => {
      const def = effDef(p, lib);
      if (!def) return;
      const carriesWorktop = def.role === "cabinet-base" ||
        (def.role === "appliance" && (def.subtype === "hob" || def.subtype === "sink"));
      if (!carriesWorktop) return;
      const onThisWall = p.wallId ? (p.wallId === wall.id) : (backWall && wall.id === backWall.id);
      if (!onThisWall) return;
      const pos = cabinetWorldPos(p, def, wls, pls, lib);
      if (!pos) return;
      const along = (pos.cx - wall.start[0]) * info.dirX + (pos.cz - wall.start[1]) * info.dirZ;
      segs.push({ start: along - def.width / 2, end: along + def.width / 2, depth: def.depth });
    });
    if (!segs.length) return;
    segs.sort((a, b) => a.start - b.start);
    let cur = null;
    segs.forEach((s) => {
      if (!cur || s.start > cur.end + 0.01) {
        if (cur) raw.push(cur);
        cur = { wallId: wall.id, start: s.start, end: s.end, depth: s.depth };
      } else {
        cur.end = Math.max(cur.end, s.end);
        cur.depth = Math.max(cur.depth, s.depth);
      }
    });
    if (cur) raw.push(cur);
  });
  if (!raw.length) return [];

  // v0.56 CORNER DETECTION via the canonical wall-topology graph (wallGraph),
  // replacing the old O(runs²) brute-force endpoint-coincidence with private
  // WORKTOP_CORNER_TOL_M.
  //
  // TWO TOLERANCES co-govern a mitre and are DELIBERATELY different — do NOT unify:
  //   • entry gate  = WORKTOP_CORNER_TOL_M (0.05 m) — a PHYSICAL fact: does the slab
  //     actually reach the wall end? (reachNode below).
  //   • joint test  = wallGraph.jointTol() (= WALL_THICKNESS + 0.05) — a TOPOLOGY
  //     fact: do these walls form one joint? (the endpoints are already clustered
  //     into one node by buildWallGraph at jointTol).
  // Different physics, different magnitudes. Byte-identical on exact-corner rooms
  // (parity harness); on IRREGULAR rooms whose corner endpoints differ by a few cm,
  // jointTol clusters them into one node so the mitre is caught (the old 5 cm near()
  // missed it).
  //
  // NB: worktops consume wallGraph NODES, NOT RunGraph.transitions — RunGraph
  // associates a run with a corner at RUN_CORNER_REACH_M (1.0 m, an intent reach),
  // whereas a worktop mitre needs the slab to physically reach the joint (~5 cm).
  // Different semantics; the node oracle is the correct one here.
  const graph = getWallGraph(wls);
  const reachNode = (run, which) => {
    const wi = graph.walls.get(run.wallId);
    if (!wi) return null;
    const along = which === "start" ? run.start : run.end;
    if (along >= WORKTOP_CORNER_TOL_M && along <= wi.len - WORKTOP_CORNER_TOL_M) return null; // mid-wall — not exposed
    return along < WORKTOP_CORNER_TOL_M ? wi.startNode : wi.endNode;                          // topology node id
  };
  // DISTINCT worktop run ENDS reaching each joint node — a run is never matched
  // against itself even if BOTH its ends land on the same node (a lone tiny/off-wall
  // run must NOT self-mitre; legacy near() excluded self-matches via `other === run`).
  //
  // Iteration 21 SEAM FIX: node co-membership alone is NOT sufficient for a mitre.
  // wallGraph nodes are TRANSITIVE union-find clusters at jointTol (wallGraph step 1),
  // so short stubs / peninsula bridges can chain two worktop-wall ends that are
  // arbitrarily far apart into ONE node (worktopAdversarial3 ATTACK B — closed by
  // ATTACK B2), spuriously marking 45° across a physical gap no slab bridges. A
  // run-end is mitred iff a DIFFERENT run has an end at the SAME node within
  // jointTol() of THIS end's WORLD endpoint. jointTol() is read at call time —
  // WALL_THICKNESS is a live mutable binding (LOCK-W-08). Exact corners (distance
  // ≈ 0) and the pinned 8 cm irregular corner (0.082 < jointTol 0.20,
  // worktopParity divergence 1) are byte-identical under this gate.
  const wallById = new Map(wls.map((w) => [w.id, w]));
  const endpointXZ = (run, which) => {
    const wall = wallById.get(run.wallId);
    const info = getWallPlacementInfo(wall, wls);
    const along = which === "start" ? run.start : run.end;
    return [wall.start[0] + info.dirX * along, wall.start[1] + info.dirZ * along];
  };
  const endsAtNode = new Map(); // nodeId → [{ ri, pt: worldEndpoint }]
  raw.forEach((run, ri) => ["start", "end"].forEach((which) => {
    const nid = reachNode(run, which);
    if (nid) {
      if (!endsAtNode.has(nid)) endsAtNode.set(nid, []);
      endsAtNode.get(nid).push({ ri, pt: endpointXZ(run, which) });
    }
  }));

  return raw.map((run, ri) => {
    const lengthMm = Math.round((run.end - run.start) * 1000);
    const depthMm = Math.round((run.depth + WORKTOP_OVERHANG_M) * 1000);
    const thicknessMm = Math.round(LHC_RULES.WORKTOP_T * 1000);
    const corners = [];
    ["start", "end"].forEach((which) => {
      const nid = reachNode(run, which);
      if (!nid) return;                 // mid-wall break — not a corner, not exposed
      const pt = endpointXZ(run, which);
      // 45° iff ANOTHER run's end shares this joint AND physically meets this end.
      const mitred = (endsAtNode.get(nid) || []).some((e) =>
        e.ri !== ri && Math.hypot(e.pt[0] - pt[0], e.pt[1] - pt[1]) < jointTol());
      corners.push({ end: which, angleDeg: mitred ? 45 : 0 });
    });
    return { wallId: run.wallId, lengthMm, depthMm, thicknessMm, corners };
  });
}

/* ── Quote modal state + math (~47968/48069/48078) ────────────────────────── */
export function createQuoteState() {
  return { multiplier: 1, discount1: 0, discount2: 0, transport: 0, assembly: 0, vatRate: 0, qtyOverrides: {} }; // VAT removed 2026-07-19
}

/** Σ effDef.price × qty (~48069). Kitchen: NO sofa fabric/leg extras. */
export function quoteSubtotal(placements, lib, quoteState) {
  const q = quoteState || createQuoteState();
  return (placements || []).reduce((s, p) => {
    const def = effDef(p, lib);
    if (!def) return s;
    const qty = q.qtyOverrides[p.id] || 1;
    return s + (def.price || 0) * qty;
  }, 0);
}

/** recomputeQuote math (~48078) — returns every intermediate the modal displays. */
export function computeQuote(placements, lib, quoteState) {
  const q = quoteState || createQuoteState();
  const listino = quoteSubtotal(placements, lib, q); // + sofaExtras(0) for kitchens
  const gross = listino * q.multiplier;
  const afterD1 = gross * (1 - q.discount1 / 100);
  const afterD2 = afterD1 * (1 - q.discount2 / 100);
  const net = afterD2 + q.transport + q.assembly;
  const vat = 0;                 // VAT does not exist (2026-07-19) — any stored vatRate is ignored
  const final = net;
  return {
    listino, gross, net, vat, final,
    multiplier: q.multiplier, discount1: q.discount1, discount2: q.discount2,
    transport: q.transport, assembly: q.assembly, vatRate: q.vatRate,
    volumeM3: gross * QUOTE_VOLUME_FACTOR, weightKg: gross * QUOTE_WEIGHT_FACTOR,
  };
}

/* ── I-1 canonical order payload (~48103) ─────────────────────────────────────
 * ctx: { orderId?, quoteState?, client? ({id,name,email,phone,address,city}),
 *        clientName?, projectName?, projectId?, now? }.
 * The iframe reads currentCustomer()/currentConfiguration()/repProjectMeta() —
 * the native host passes the same facts explicitly. */
export function buildOrderPayload(state, lib, ctx = {}) {
  const q = ctx.quoteState || createQuoteState();
  const now = ctx.now || new Date();
  const cust = ctx.client || null;
  const meta = {
    client: ctx.clientName || (cust && cust.name) || "—",
    project: ctx.projectName || "Project",
    date: now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
  };
  const placements = (state && state.placements) || [];
  const wls = (state && state.walls) || [];
  const kw = (state && state.kitchenWizard) || {};

  // ── Pricing — same formula as recomputeQuote(), single source of truth ──
  const pr = computeQuote(placements, lib, q);

  // ── Lines — grouped by type; custom-sized get individual lines (v0.29) ──
  const grouped = {};
  placements.forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    const catDef = lib[p.type];
    const isCustom = isCustomSized(p);
    const surcharge = isCustom ? Math.round((catDef.price || 0) * (getPricing().customSizePct / 100)) : 0;
    const qty = q.qtyOverrides[p.id] || 1;
    const key = isCustom ? "custom|" + p.id : p.type;
    const note = isCustom ? sizingNote(p, lib) : "";
    if (!grouped[key]) {
      grouped[key] = {
        code: isCustom ? (catDef.code || p.type) + "*" : (def.code || p.type),
        name: isCustom ? (catDef.name || p.type) + " — " + customDimLabel(p) : (def.name || p.type),
        role: def.role || "",
        custom: isCustom,
        dims: { w: Math.round((def.width || 0) * 1000), d: Math.round((def.depth || 0) * 1000), h: Math.round((def.height || 0) * 1000) },
        unitPrice: (catDef.price || 0) + surcharge,
        qty: 0,
        _productionNote: note,
      };
    }
    grouped[key].qty += qty;
  });
  const lines = Object.values(grouped).map((g) => ({ ...g, lineTotal: g.unitPrice * g.qty }));

  // ── Volume & weight (Reports KPI logic) ──
  let volume = 0;
  placements.forEach((p) => {
    const def = effDef(p, lib);
    if (def) volume += def.width * def.depth * def.height;
  });
  const weightKg = Math.round(volume * REP_SHIP_DENSITY);

  // ── Room ──
  const room = {
    walls: wls.map((w) => ({
      id: w.id,
      start: w.start, end: w.end,
      height: w.height || DEFAULT_WALL_HEIGHT,
      lengthM: +Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]).toFixed(3),
    })),
    upperLine: +getUpperLineM().toFixed(3),
    ceilingH: wls.length ? Math.max(...wls.map((w) => w.height || DEFAULT_WALL_HEIGHT)) : null,
    // F-3: windows & doors, with clear field names for export consumers
    // (internal UI/3D code uses op.u/op.sill — aliased here to offsetM/sillM).
    openings: ((state && state.wallOpenings) || []).map((op) => ({
      id: op.id, wallId: op.wallId, type: op.type,
      offsetM: +op.u.toFixed(3), widthM: +op.width.toFixed(3), heightM: +op.height.toFixed(3),
      sillM: op.type === "window" ? +(op.sill != null ? op.sill : OPENING_DEFAULTS.windowSillM).toFixed(3) : 0,
      hinge: op.type === "door" ? (op.hinge === "right" ? "right" : "left") : undefined,
      open: op.type === "door" ? op.open !== false : undefined,
    })),
  };

  // ── Finishes — current kitchen wizard selections ──
  const finishes = {
    shell: kw.shell, front: kw.front, worktop: kw.worktop, handle: kw.handle, plinth: kw.plinth,
    door: kw.door, doorColor: kw.doorColor, carcass: kw.carcass, handleColor: kw.handleColor,
    wallFinish: kw.wallFinish, ceilingFinish: kw.ceilingFinish,
  };

  // ── Iteration 21 VISIBILITY: technical validation (ADDITIVE key) ──
  // Derived from the SAME state via the read-only rule engine (lib rides INSIDE
  // the input object — buildRuleContext reads input.lib). passed:true findings
  // are measurements/telemetry (ruleEngine.js finding shape) and are EXCLUDED
  // from both counts and findings. Severities are 'error'|'warning'|'info' ONLY.
  // counts are computed over ALL failed findings BEFORE capping; findings are
  // stable-sorted by severity rank (error<warning<info — Array.sort is stable,
  // registry order preserved within rank) THEN capped at 50, so errors always
  // survive truncation. Deterministic: same state → same payload.validation.
  const SEV_RANK = { error: 0, warning: 1, info: 2 };
  const VALIDATION_FINDINGS_CAP = 50;
  const failed = evaluateRules({ walls: wls, placements, lib }).findings.filter((f) => !f.passed);
  const valCounts = { error: 0, warning: 0, info: 0 };
  failed.forEach((f) => { if (valCounts[f.severity] !== undefined) valCounts[f.severity] += 1; });
  const valFindings = failed
    .slice()
    .sort((a, b) => (SEV_RANK[a.severity] ?? 3) - (SEV_RANK[b.severity] ?? 3))
    .slice(0, VALIDATION_FINDINGS_CAP)
    .map((f) => ({ ruleId: f.ruleId, severity: f.severity, message: f.message }));

  return {
    project: {
      id: ctx.orderId || ctx.projectId || null,
      client: meta.client, project: meta.project, date: meta.date,
    },
    client: cust ? {
      id: cust.id, name: cust.name, email: cust.email, phone: cust.phone,
      address: cust.address, city: cust.city,
    } : null,
    pricing: {
      listino: pr.listino, gross: pr.gross, multiplier: pr.multiplier,
      discount1: pr.discount1, discount2: pr.discount2,
      net: pr.net, transport: pr.transport, assembly: pr.assembly,
      vatRate: pr.vatRate, vat: pr.vat, final: pr.final,
    },
    lines,
    worktops: getWorktopRuns(state, lib),
    endPanels: buildEndPanels(state.walls, state.placements, lib),
    fillers: buildFillerSchedule(state.walls, state.placements, lib),
    validation: { counts: valCounts, findings: valFindings },
    finishes,
    room,
    volume: +volume.toFixed(3),
    weightKg,
    generatedAt: now.toISOString(),
  };
}

/* ── I-1/I-2 Preventivo PDF (~48211) — VERBATIM printable HTML template ────── */
export function buildPreventivoDoc(payload) {
  const fmt = (n) => "€ " + (n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const today = new Date(payload.generatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const p = payload.pricing;

  const rows = (payload.lines || []).map((l) => `
    <tr>
      <td><strong>${l.code}</strong></td>
      <td>${l.name}${l.custom ? ' <span style="color:#f59e0b;font-size:11px">(custom)</span>' : ""}</td>
      <td style="text-align:center">${l.qty}</td>
      <td>${l.dims.w}×${l.dims.d}×${l.dims.h}</td>
      <td style="text-align:right">${fmt(l.unitPrice)}</td>
      <td style="text-align:right;font-weight:700">${fmt(l.lineTotal)}</td>
    </tr>`).join("");

  // ── Iteration 21 VISIBILITY sections — Worktops / End panels / Fillers /
  // Technical validation, rendered AFTER Modules+Price using ONLY the template's
  // existing vocabulary (.section, h2, table/th/td, .row, .label, muted #6B7280,
  // the amber accent span, inline right/center alignment — no new CSS classes,
  // fonts or colors). A section is OMITTED entirely when its array is empty or
  // its key is absent (captured payloads predate endPanels/fillers/validation —
  // a legacy payload renders the legacy document byte-identically). All mm
  // values are ALREADY integers in the payload — printed verbatim, never fmt()
  // (fmt is money-only).
  const roomWalls = (payload.room && payload.room.walls) || [];
  const wallLabel = (wallId) => {
    const idx = roomWalls.findIndex((w) => w.id === wallId);
    return idx >= 0 ? "Wall " + (idx + 1) : wallId; // raw ids never print for room walls
  };
  const sections = [];

  const worktops = payload.worktops || [];
  if (worktops.length) {
    const wtRows = worktops.map((w) => `
    <tr>
      <td>${wallLabel(w.wallId)}</td>
      <td style="text-align:right">${w.lengthMm}</td>
      <td style="text-align:right">${w.depthMm}</td>
      <td style="text-align:right">${w.thicknessMm}</td>
      <td>${(w.corners || []).filter((c) => c.angleDeg === 45).map((c) => "45° " + c.end).join(" · ") || "—"}</td>
    </tr>`).join("");
    sections.push(`  <div class="section">
    <h2>Worktops</h2>
    <table>
      <thead><tr><th>Wall</th><th style="text-align:right">Length</th><th style="text-align:right">Depth</th><th style="text-align:right">Thickness (mm)</th><th>Corners</th></tr></thead>
      <tbody>${wtRows}</tbody>
    </table>
  </div>`);
  }

  const endPanels = payload.endPanels || [];
  if (endPanels.length) {
    const epRows = endPanels.map((ep) => `
    <tr>
      <td>${wallLabel(ep.wallId)}</td>
      <td>${ep.end}</td>
      <td>${ep.role}</td>
      <td style="text-align:right">${ep.heightMm}</td>
      <td style="text-align:right">${ep.depthMm}</td>
    </tr>`).join("");
    sections.push(`  <div class="section">
    <h2>End panels</h2>
    <table>
      <thead><tr><th>Wall</th><th>End</th><th>Role</th><th style="text-align:right">Height</th><th style="text-align:right">Depth (mm)</th></tr></thead>
      <tbody>${epRows}</tbody>
    </table>
  </div>`);
  }

  const fillers = payload.fillers || [];
  if (fillers.length) {
    const fiRows = fillers.map((f) => `
    <tr>
      <td>${wallLabel(f.wallId)}</td>
      <td>${f.kind}</td>
      <td>${f.line}</td>
      <td style="text-align:right">${f.widthMm}</td>
      <td style="text-align:right">${f.heightMm}</td>
    </tr>`).join("");
    sections.push(`  <div class="section">
    <h2>Fillers</h2>
    <table>
      <thead><tr><th>Wall</th><th>Kind</th><th>Line</th><th style="text-align:right">Width</th><th style="text-align:right">Height (mm)</th></tr></thead>
      <tbody>${fiRows}</tbody>
    </table>
  </div>`);
  }

  // Technical validation renders ONLY when payload.validation exists (legacy
  // payloads → omitted). Severity tagging reuses the template's amber accent
  // span (error additionally font-weight:700); info stays .label muted — the
  // template has no red token and none is introduced.
  const v = payload.validation;
  if (v) {
    const c = v.counts || {};
    const vFindings = v.findings || [];
    let vBody;
    if (!vFindings.length) {
      vBody = `\n    <div class="row"><span class="label">All checks passed — no findings</span></div>`;
    } else {
      const parts = [];
      if (c.error) parts.push(c.error + (c.error === 1 ? " error" : " errors"));
      if (c.warning) parts.push(c.warning + (c.warning === 1 ? " warning" : " warnings"));
      if (c.info) parts.push(c.info + " info");
      const sevTag = (sev) => sev === "error"
        ? '<span style="color:#f59e0b;font-size:11px;font-weight:700">error</span> '
        : sev === "warning"
          ? '<span style="color:#f59e0b;font-size:11px">warning</span> '
          : "";
      vBody = `\n    <div class="row"><span class="label">${parts.join(" · ")}</span></div>` +
        vFindings.map((f) => `\n    <div class="row"><span class="label">${sevTag(f.severity)}${f.ruleId} — ${f.message}</span></div>`).join("");
    }
    sections.push(`  <div class="section">\n    <h2>Technical validation</h2>${vBody}\n  </div>`);
  }

  // "" when every section is empty → the document below is byte-identical to
  // the pre-Iteration-21 template (surgical byte-pin in outputs.test.js).
  const extraSections = sections.map((s) => "\n" + s + "\n").join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Preventivo — ${payload.project.project}</title>
<style>
  *{box-sizing:border-box;}
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 48px 56px; color: #18181B; max-width: 760px; margin: 0 auto; line-height: 1.5; }
  .logo { font-weight: 700; letter-spacing: 0.06em; font-size: 28px; color: #2D7FF9; }
  .sub { font-size: 10px; color: #6B7280; letter-spacing: 0.20em; margin-bottom: 32px; font-weight: 500; }
  h1 { font-size: 22px; font-weight: 600; margin: 0 0 4px; }
  .ref { color: #6B7280; font-size: 12px; margin-bottom: 28px; }
  .section { margin-bottom: 28px; }
  .section h2 { font-size: 10px; font-weight: 600; color: #6B7280; letter-spacing: 0.16em; text-transform: uppercase; margin: 0 0 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; font-size: 10px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 1px solid #E5E5E7; padding: 6px 8px; }
  td { padding: 8px; border-bottom: 1px solid #F2F2F4; }
  .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
  .label { color: #6B7280; }
  .total { font-size: 18px; font-weight: 600; border-top: 1px solid #E5E5E7; padding-top: 14px; margin-top: 10px; }
  .subtotal { border-top: 1px solid #E5E5E7; padding-top: 12px; margin-top: 8px; font-weight: 500; }
  @media print { body { padding: 24px; } @page { margin: 1.5cm; } }
</style>
</head>
<body>
  <div class="logo">LHC</div>
  <div class="sub">LHC STUDIO · NEW HOME MOBILERI</div>
  <h1>${payload.project.project}</h1>
  <div class="ref">${payload.project.client} &nbsp;·&nbsp; ${today}</div>

  <div class="section">
    <h2>Modules</h2>
    <table>
      <thead><tr><th>Code</th><th>Description</th><th style="text-align:center">Qty</th><th>W×D×H (mm)</th><th style="text-align:right">Unit</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#999;">No modules</td></tr>'}</tbody>
    </table>
  </div>

  <div class="section">
    <h2>Price</h2>
    <div class="row"><span class="label">Listino</span><span>${fmt(p.listino)}</span></div>
    <div class="row"><span class="label">Multiplier ×${p.multiplier}</span><span>${fmt(p.gross)}</span></div>
    ${p.discount1 ? `<div class="row"><span class="label">Discount 1 (${p.discount1}%)</span><span>−${fmt(p.gross * p.discount1 / 100)}</span></div>` : ""}
    ${p.discount2 ? `<div class="row"><span class="label">Discount 2 (${p.discount2}%)</span><span>−${fmt((p.gross * (1 - p.discount1 / 100)) * p.discount2 / 100)}</span></div>` : ""}
    ${p.transport ? `<div class="row"><span class="label">Transport</span><span>${fmt(p.transport)}</span></div>` : ""}
    ${p.assembly ? `<div class="row"><span class="label">Assembly</span><span>${fmt(p.assembly)}</span></div>` : ""}
    <div class="row subtotal"><span>Net</span><span>${fmt(p.net)}</span></div>
    <div class="row total"><span>Total</span><span>${fmt(p.final)}</span></div>
  </div>
${extraSections}
  <script>setTimeout(function(){window.print()}, 250);</script>
</body>
</html>`;
}

/* ── buildQuotePDF (~39104) — the downloadable quote HTML document ────────────
 * Pure part: returns { quoteN, fileName, html, totals }. The totals math is the
 * VERBATIM 39110–39115 sequence, which equals pricing.quoteTotals by construction
 * (linesSubtotal·(1−disc) → +tax). ctx: { clientName, projectName, now? }. */
export function buildQuotePdfDoc(state, lib, ctx = {}) {
  const kw = (state && state.kitchenWizard) || {};
  const { autoLines, manualLines } = buildQuoteLines((state && state.placements) || [], lib, kw, (state && state.walls) || [], ctx.blob || null, (state && state.wallOpenings) || []);
  const allLines = autoLines.concat(manualLines);
  const pg = kw.priceGroup || "G1";
  const pgLabels = { G1: "G1 — Essential", G2: "G2 — Standard", G3: "G3 — Premium", G4: "G4 — Exclusive" };
  const overallDisc = kw.quoteDiscount || 0;
  const linesSubtotal = allLines.reduce((s, l) => s + (l.lineTotal || 0), 0);
  const subtotal = linesSubtotal * (1 - overallDisc);
  const taxRate = 0;                        // VAT does not exist (2026-07-19)
  const taxAmt = 0;
  const total = subtotal;
  const clientName = ctx.clientName || "—";
  const projectName = ctx.projectName || "—";
  const now = ctx.now || new Date();
  const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const quoteN = "QT-" + dateStr.replace(/\//g, "") + "-" + String((kw.quoteVersions || []).length + 1).padStart(3, "0");
  const fmt = (n) => "€ " + (n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const lineRows = allLines.map((l) => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#6b7280;">${l.code || ""}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;">${l.description}${l.dims ? `<div style="font-size:10px;color:#9ca3af;">${l.dims}</div>` : ""}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;">${l.qty}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;">${l.unitPrice > 0 ? fmt(l.unitPrice) : "—"}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;">${l.discount ? l.discount + "%" : "—"}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;font-weight:600;">${l.lineTotal > 0 ? fmt(l.lineTotal) : "—"}</td>
    </tr>`).join("");

  const html = `<!DOCTYPE html>
<html lang="en"><head>
<meta charset="UTF-8"><title>Quote ${quoteN}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #111; background: #fff; padding: 32px 40px; }
  .hdr { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; border-bottom: 2px solid #111; padding-bottom: 20px; }
  .brand { font-size: 22px; font-weight: 700; letter-spacing: .03em; }
  .brand span { font-weight: 300; color: #555; }
  .meta { text-align: right; font-size: 12px; color: #555; line-height: 1.7; }
  .meta strong { color: #111; font-size: 13px; display: block; }
  .client-block { margin-bottom: 28px; font-size: 13px; line-height: 1.8; }
  .client-block .label { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #888; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  thead tr { background: #f3f4f6; }
  th { padding: 8px 8px; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #6b7280; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
  th:last-child, th:nth-child(3), th:nth-child(4), th:nth-child(5) { text-align: right; }
  .totals { margin-left: auto; width: 280px; }
  .totals-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; color: #555; }
  .totals-row.final { font-size: 15px; font-weight: 700; color: #111; border-top: 2px solid #111; padding-top: 10px; margin-top: 4px; }
  .note { margin-top: 32px; font-size: 10.5px; color: #888; border-top: 1px solid #e5e7eb; padding-top: 14px; }
  @media print { body { padding: 0; } }
</style>
</head><body>
  <div class="hdr">
    <div class="brand">
      <svg width="110" height="32" viewBox="0 0 110 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <text x="0" y="24" font-family="Helvetica Neue,Helvetica,Arial,sans-serif" font-size="26" font-weight="700" letter-spacing="1" fill="#111">LHC</text>
        <text x="72" y="24" font-family="Helvetica Neue,Helvetica,Arial,sans-serif" font-size="14" font-weight="300" fill="#555">Studio</text>
      </svg>
    </div>
    <div class="meta">
      <strong>${quoteN}</strong>
      ${dateStr}<br>
      Price group: ${pgLabels[pg] || pg}
    </div>
  </div>
  <div class="client-block">
    <div class="label">Client</div>
    <strong>${clientName}</strong>
    ${projectName}
  </div>
  <table>
    <thead><tr>
      <th>Code</th><th>Description</th><th>Qty</th><th>Unit price</th><th>Disc%</th><th>Total</th>
    </tr></thead>
    <tbody>${lineRows}</tbody>
  </table>
  <div class="totals">
    <div class="totals-row"><span>Lines subtotal</span><span>${fmt(linesSubtotal)}</span></div>
    ${overallDisc > 0 ? `<div class="totals-row"><span>Discount ${Math.round(overallDisc * 100)}%</span><span>− ${fmt(linesSubtotal * overallDisc)}</span></div>` : ""}
    <div class="totals-row"><span>Net subtotal</span><span>${fmt(subtotal)}</span></div>
    <div class="totals-row"><span>Tax ${Math.round(taxRate * 100)}%</span><span>${fmt(taxAmt)}</span></div>
    <div class="totals-row final"><span>Total (incl. tax)</span><span>${fmt(total)}</span></div>
  </div>
  <div class="note">Prices are subject to final confirmation. Valid for 30 days from date of issue.</div>
</body></html>`;

  return { quoteN, fileName: quoteN + ".html", html, totals: { linesSubtotal, subtotal, taxRate, taxAmt, total } };
}

/** DOM tail of buildQuotePDF (~39196): blob → <a download> click. */
export function downloadQuotePDF(state, lib, ctx) {
  const { fileName, html } = buildQuotePdfDoc(state, lib, ctx);
  if (typeof document === "undefined") return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/* ── buildPrintHTML (~41197) — A4 Quotation print doc for saved quotes ────────
 * selectedQuotes: [{ id, project, description, lineItems, amountMinor, validUntil,
 * signature, signedAt }] (client-quote records — amounts in MINOR units).
 * ctx: { company?, customer?, profileName?, now? } replaces loadUserProfile()/
 * data.customers[viewedCustomerId]. */
export function buildPrintHTML(selectedQuotes, ctx = {}) {
  const co = ctx.company || {};
  const cust = ctx.customer || null;
  const lang = co.quoteLang || "sq";
  const deposit = co.quoteDeposit || "70";
  const now = ctx.now || new Date();
  const today = now.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const quoteRef = selectedQuotes.length === 1 ? selectedQuotes[0].id : "S" + String(Math.floor(Math.random() * 99999)).padStart(5, "0");
  const salesperson = ctx.profileName || co.name || "LHC Studio";

  const validityDays = parseInt(co.quoteValidity || "30", 10);
  const expiryDate = selectedQuotes[0] && selectedQuotes[0].validUntil
    ? new Date(selectedQuotes[0].validUntil)
    : new Date(now.getTime() + validityDays * 86400000);
  const expiry = expiryDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

  const curSym = (code) => ({ EUR: "€", USD: "$", GBP: "£", CHF: "CHF", ALL: "L" }[code] || code || "€");
  const currSym = curSym(co.currency);
  const fmtAmt = (v) => (v / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + currSym;

  const footerNotes = {
    sq: `"Ju lutemi, shikoni me kujdes të gjitha detajet e porosisë përpara konfirmimit, pasi pas miratimit kompania nuk mban përgjegjësi për pasaktësi në artikuj, sasi apo specifika të porositura. Për t'u konsideruar e vlefshme, çdo porosi kërkon një pagesë paraprake prej ${deposit}% të vlerës totale, ndërsa ${100 - parseInt(deposit, 10)}% e mbetur duhet të shlyhet në momentin e dorëzimit të mallit."`,
    en: `"Please review all order details carefully before confirming. Once approved, the company is not responsible for inaccuracies in items, quantities, or specifications. A deposit of ${deposit}% is required to confirm the order, with the remaining ${100 - parseInt(deposit, 10)}% due upon delivery."`,
    it: `"Si prega di verificare attentamente tutti i dettagli dell'ordine prima della conferma. Una volta approvato, l'azienda non è responsabile per imprecisioni negli articoli o nelle specifiche. Un acconto del ${deposit}% è richiesto per confermare l'ordine, con il restante ${100 - parseInt(deposit, 10)}% dovuto alla consegna."`,
  };
  const footerText = co.quoteFooter || footerNotes[lang] || footerNotes["sq"];

  const detectCat = (q) => {
    const t = ((q.project || "") + (q.description || "")).toLowerCase();
    if (t.includes("sofa") || t.includes("kend") || t.includes("ndenj")) return "Dhoma e Ndenjes";
    if (t.includes("gjum") || t.includes("bedroom") || t.includes("dollap") || t.includes("wardrobe")) return "Dhoma Gjumit";
    if (t.includes("tualeti") || t.includes("bathroom")) return "Tualeti";
    return "Kuzhina";
  };

  const catOrder = ["Kuzhina", "Dhoma e Ndenjes", "Dhoma Gjumit", "Tualeti"];
  const grouped = {};
  let mIdx = 1;
  selectedQuotes.forEach((q) => {
    const items = (q.lineItems || []).length ? q.lineItems : [{ description: q.project, qty: 1, unitPrice: q.amountMinor || 0, discount: 0 }];
    items.forEach((li) => {
      const cat = detectCat({ project: q.project, description: li.description || "" });
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({ ...li, mcode: "M" + mIdx++ });
    });
  });
  const usedCats = [...catOrder.filter((c) => grouped[c]), ...Object.keys(grouped).filter((c) => !catOrder.includes(c))];
  const grandTotal = Object.values(grouped).flat().reduce((s, li) => s + Math.round(li.unitPrice * li.qty * (1 - (li.discount || 0) / 100)), 0);

  const logo = `<svg width="90" height="62" viewBox="0 0 90 62" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="22" height="22" stroke="#111" stroke-width="1.8" fill="none"/>
    <line x1="1" y1="12" x2="23" y2="12" stroke="#111" stroke-width="1.8"/>
    <rect x="26" y="1" width="22" height="22" stroke="#111" stroke-width="1.8" fill="none"/>
    <line x1="37" y1="1" x2="37" y2="23" stroke="#111" stroke-width="1.8"/>
    <path d="M51 1 Q63 1 63 12 Q63 23 51 23" stroke="#111" stroke-width="1.8" fill="none"/>
    <text x="0" y="42" font-family="Arial,Helvetica,sans-serif" font-size="5.5" letter-spacing="2.5" fill="#aaa" text-anchor="start">LUGANO</text>
    <text x="24" y="42" font-family="Arial,Helvetica,sans-serif" font-size="5.5" letter-spacing="2.5" fill="#aaa" text-anchor="start">HOME</text>
    <text x="46" y="42" font-family="Arial,Helvetica,sans-serif" font-size="5.5" letter-spacing="2.5" fill="#aaa" text-anchor="start">COLLECTION</text>
  </svg>`;

  const sectionsHTML = usedCats.map((cat) => {
    const items = grouped[cat];
    const catTot = items.reduce((s, li) => s + Math.round(li.unitPrice * li.qty * (1 - (li.discount || 0) / 100)), 0);
    const rows = items.map((li) => {
      const lt = Math.round(li.unitPrice * li.qty * (1 - (li.discount || 0) / 100));
      return `<tr>
        <td style="padding:11px 16px 11px 0;vertical-align:top;border-bottom:1px solid #efefef;width:52%;">
          <div style="font-size:11.5px;font-weight:700;color:#111;margin-bottom:3px;">${li.mcode || li.code || ""}</div>
          <div style="font-size:11.5px;color:#444;line-height:1.6;">${li.description || ""}</div>
        </td>
        <td style="padding:11px 12px;text-align:center;font-size:12px;border-bottom:1px solid #efefef;color:#555;white-space:nowrap;">${Number(li.qty || 1).toFixed(2)}&nbsp;Cope</td>
        <td style="padding:11px 12px;text-align:right;font-size:12px;border-bottom:1px solid #efefef;color:#555;white-space:nowrap;">${((li.unitPrice || 0) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
        <td style="padding:11px 12px;text-align:center;font-size:12px;border-bottom:1px solid #efefef;color:#999;">0%</td>
        <td style="padding:11px 0 11px 12px;text-align:right;font-size:12px;border-bottom:1px solid #efefef;font-weight:600;color:#111;white-space:nowrap;">${fmtAmt(lt)}</td>
      </tr>`;
    }).join("");
    return `<tr style="background:#f7f7f7;">
        <td colspan="4" style="padding:9px 0 9px 0;border-top:2px solid #ddd;border-bottom:1px solid #ddd;">
          <span style="font-size:12.5px;font-weight:800;color:#111;letter-spacing:.01em;">${cat}</span>
        </td>
        <td style="padding:9px 0 9px 12px;text-align:right;font-size:12.5px;font-weight:800;border-top:2px solid #ddd;border-bottom:1px solid #ddd;color:#111;white-space:nowrap;">${fmtAmt(catTot)}</td>
      </tr>${rows}`;
  }).join("");

  const docBody = `<div style="font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;width:210mm;min-height:297mm;padding:18mm 16mm 16mm;color:#111;background:#fff;box-sizing:border-box;">

    <!-- HEADER -->
    <table style="width:100%;border-collapse:collapse;margin-bottom:14mm;">
      <tr>
        <td style="vertical-align:top;padding:0;">${logo}</td>
        <td style="vertical-align:top;text-align:right;padding:0;">
          <div style="font-size:12px;color:#555;line-height:1.7;">${co.address || ""}</div>
          <div style="font-size:12px;color:#555;line-height:1.7;">${co.city || "Kotarja-Tirane"}</div>
          <div style="font-size:11px;color:#999;margin-top:4px;">${co.email || ""}</div>
          <div style="font-size:11px;color:#999;">${co.phone || ""}</div>
        </td>
      </tr>
    </table>

    <!-- TITLE BAR -->
    <div style="border-top:3px solid #111;border-bottom:1px solid #ddd;padding:10px 0;margin-bottom:8mm;">
      <div style="font-size:28px;font-weight:300;letter-spacing:-.02em;color:#111;">Quotation <span style="font-weight:700;"># ${quoteRef}</span></div>
    </div>

    <!-- META ROW -->
    <table style="width:100%;border-collapse:collapse;margin-bottom:8mm;border-bottom:1px solid #e8e8e8;">
      <tr>
        <td style="padding:0 0 10px;vertical-align:top;width:33%;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#999;margin-bottom:5px;">Quotation Date</div>
          <div style="font-size:13px;font-weight:600;color:#111;">${today}</div>
        </td>
        <td style="padding:0 0 10px;vertical-align:top;width:33%;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#999;margin-bottom:5px;">Expiration</div>
          <div style="font-size:13px;font-weight:600;color:#111;">${expiry}</div>
        </td>
        <td style="padding:0 0 10px;vertical-align:top;width:34%;text-align:right;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#999;margin-bottom:5px;">Salesperson</div>
          <div style="font-size:13px;font-weight:600;color:#111;">${salesperson}</div>
        </td>
      </tr>
    </table>

    ${cust ? `<!-- CLIENT -->
    <div style="margin-bottom:8mm;padding:10px 12px;background:#f5f5f5;border-left:3px solid #111;">
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#999;margin-bottom:4px;">Prepared for</div>
      <div style="font-size:14px;font-weight:700;color:#111;">${cust.name}</div>
      <div style="font-size:12px;color:#555;margin-top:2px;">${cust.email || ""} ${cust.city ? "&nbsp;&middot;&nbsp;" + cust.city : ""}</div>
    </div>` : ""}

    <!-- ITEMS TABLE -->
    <table style="width:100%;border-collapse:collapse;margin-bottom:6mm;">
      <thead>
        <tr style="border-top:2px solid #111;border-bottom:1px solid #111;">
          <th style="padding:8px 16px 8px 0;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#777;">Description</th>
          <th style="padding:8px 12px;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#777;">Quantity</th>
          <th style="padding:8px 12px;text-align:right;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#777;">Unit Price</th>
          <th style="padding:8px 12px;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#777;">Taxes</th>
          <th style="padding:8px 0 8px 12px;text-align:right;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#777;">Amount</th>
        </tr>
      </thead>
      <tbody>${sectionsHTML}</tbody>
    </table>

    <!-- TOTALS -->
    <table style="width:100%;border-collapse:collapse;margin-bottom:10mm;">
      <tr>
        <td style="width:55%;padding:0;"></td>
        <td style="width:45%;padding:0;">
          <table style="width:100%;border-collapse:collapse;">
            <tr>
              <td style="padding:7px 0;font-size:12px;color:#555;border-top:1px solid #e8e8e8;">Untaxed Amount</td>
              <td style="padding:7px 0;text-align:right;font-size:12px;font-weight:500;color:#111;border-top:1px solid #e8e8e8;">${fmtAmt(grandTotal)}</td>
            </tr>
            <tr>
              <td style="padding:7px 0;font-size:12px;color:#555;border-top:1px solid #e8e8e8;">TVSH</td>
              <td style="padding:7px 0;text-align:right;font-size:12px;color:#555;border-top:1px solid #e8e8e8;">0.00 L</td>
            </tr>
            <tr>
              <td style="padding:10px 0 4px;font-size:15px;font-weight:800;color:#111;border-top:2px solid #111;">Total</td>
              <td style="padding:10px 0 4px;text-align:right;font-size:15px;font-weight:800;color:#111;border-top:2px solid #111;">${fmtAmt(grandTotal)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    <!-- FOOTER NOTE -->
    <div style="border-top:1px solid #e0e0e0;padding-top:10px;margin-bottom:8mm;">
      <p style="font-size:10.5px;color:#666;line-height:1.85;margin:0;">${footerText}</p>
    </div>

    ${selectedQuotes[0] && selectedQuotes[0].signature ? `
    <!-- CLIENT SIGNATURE -->
    <div style="margin-top:6mm;display:grid;grid-template-columns:1fr 1fr;gap:24px;">
      <div>
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#999;margin-bottom:8px;">Client signature &amp; approval</div>
        <img src="${selectedQuotes[0].signature}" style="max-width:200px;height:60px;object-fit:contain;border-bottom:1px solid #111;display:block;">
        <div style="font-size:10px;color:#999;margin-top:4px;">Signed ${selectedQuotes[0].signedAt ? new Date(selectedQuotes[0].signedAt).toLocaleDateString("en-GB") : today}</div>
      </div>
      <div>
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#999;margin-bottom:8px;">Dealer</div>
        <div style="border-bottom:1px solid #111;height:60px;"></div>
        <div style="font-size:10px;color:#999;margin-top:4px;">${salesperson}</div>
      </div>
    </div>` : ""}

    <!-- PAGE FOOTER -->
    <div style="position:absolute;bottom:12mm;left:16mm;right:16mm;border-top:1px solid #e0e0e0;padding-top:6px;display:flex;justify-content:space-between;align-items:center;">
      <span style="font-size:9px;color:#bbb;letter-spacing:.05em;text-transform:uppercase;">${co.name || "LHC STUDIO"} &middot; ${co.city || ""}</span>
      <span style="font-size:9px;color:#bbb;">Page 1 / 1</span>
    </div>

  </div>`;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Quotation # ${quoteRef}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      html,body{width:210mm;background:#fff;}
      @page{size:A4 portrait;margin:0;}
      @media print{html,body{width:210mm;}.no-print{display:none!important;}}
    </style>
  </head><body>${docBody}</body></html>`;
}

/* ── Reports page data (renderReportsPage ~20363 + rep sub-views) ─────────── */

/** repProjectModules (~20526): [{ p, def }] with effDef applied. */
export function reportModules(state, lib) {
  return ((state && state.placements) || [])
    .map((p) => ({ p, def: effDef(p, lib) }))
    .filter((x) => x.def);
}

/** Order-entry grouping shared by overview KPI 1 + Order Entry doc (~20376/20569):
 *  identical types collapse into qty rows; custom-sized get individual lines +20%. */
export function reportGroupedLines(state, lib) {
  const mods = reportModules(state, lib);
  const grouped = {};
  mods.forEach(({ def, p }) => {
    const catDef = lib[p.type] || def;
    const isCustom = isCustomSized(p);
    const surcharge = isCustom ? Math.round((catDef.price || 0) * (getPricing().customSizePct / 100)) : 0;
    const key = isCustom ? "custom|" + p.id : p.type;
    if (!grouped[key]) grouped[key] = { def, qty: 0, fee: surcharge, unit: (catDef.price || 0) + surcharge, isCustom, p };
    grouped[key].qty++;
  });
  return Object.values(grouped);
}

/** Overview KPIs + cards (~20363–20509). Quotes table data comes from the host. */
export function reportOverview(state, lib) {
  const mods = reportModules(state, lib);
  const entries = reportGroupedLines(state, lib);
  let subtotal = 0;
  entries.forEach((g) => { subtotal += g.unit * g.qty; });
  const total = subtotal;        // VAT does not exist (2026-07-19) — was ×1.22

  let vol = 0;
  mods.forEach(({ def }) => { vol += def.width * def.depth * def.height; });
  const weightKg = vol * REP_SHIP_DENSITY;

  const ws = (state && state.walls) || [];
  let run = 0;
  ws.forEach((w) => { run += Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]); });

  const roleCounts = {};
  const KNOWN_ROLES = ["cabinet-base", "cabinet-wall", "cabinet-stack", "cabinet-bridge", "cabinet-tall", "appliance"];
  mods.forEach(({ def }) => {
    const key = KNOWN_ROLES.includes(def.role) ? def.role : "other";
    roleCounts[key] = (roleCounts[key] || 0) + 1;
  });

  // Selected finishes: most-picked value per config field (~20466)
  const FIN_FIELDS = [["door", "Door style"], ["doorColor", "Door colour"], ["carcass", "Carcass"], ["handleColor", "Handles"], ["plinth", "Plinth"]];
  const finishes = [];
  FIN_FIELDS.forEach(([field, label]) => {
    const counts = {};
    mods.forEach(({ p }) => {
      const v = p.config && p.config[field];
      if (v) counts[v] = (counts[v] || 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (top.length) finishes.push({ label, value: top[0][0], more: top.length - 1 });
  });

  return {
    moduleCount: mods.length,
    distinctTypes: entries.length,
    subtotal, total,
    volumeM3: vol, weightKg,
    wallCount: ws.length, wallRunM: run,
    roleCounts, finishes,
    entries, // [{ def, qty, fee, unit, isCustom, p }]
  };
}

/** Order Entry doc rows + totals (~20553 repRenderOrderEntry). */
export function reportOrderEntry(state, lib) {
  const entries = reportGroupedLines(state, lib);
  let subtotal = 0;
  const rows = entries.map((g, i) => {
    const lineTotal = g.unit * g.qty;
    subtotal += lineTotal;
    const eff = g.isCustom ? effDef(g.p, lib) : g.def;
    const d = eff || g.def;
    return {
      n: i + 1,
      code: (g.def.code || "—") + (g.isCustom ? "*" : ""),
      name: g.def.name || "",
      isCustom: g.isCustom,
      customLabel: g.isCustom ? customDimLabel(g.p) : "",
      note: g.isCustom ? sizingNote(g.p, lib) : "",
      qty: g.qty,
      dims: Math.round(d.width * 1000) + "×" + Math.round(d.depth * 1000) + "×" + Math.round(d.height * 1000),
      unit: g.unit,
      lineTotal,
    };
  });
  const vat = 0;                             // VAT does not exist (2026-07-19)
  return { rows, subtotal, vat, total: subtotal };
}

/** Volume & Shipping (~20598 repRenderVolume). */
export function reportVolume(state, lib) {
  const mods = reportModules(state, lib);
  let totalVol = 0, totalWeight = 0;
  const rows = mods.map(({ def }, i) => {
    const vol = def.width * def.depth * def.height;
    const wt = vol * REP_SHIP_DENSITY;
    totalVol += vol; totalWeight += wt;
    return { n: i + 1, code: def.code || "—", name: def.name || "", vol, wt, pkg: (i + 1) + " / " + mods.length };
  });
  let vehicle;
  if (totalVol < 3) vehicle = "Van (up to 3 m³)";
  else if (totalVol < 15) vehicle = "Small truck (3–15 m³)";
  else if (totalVol < 40) vehicle = "Box truck (15–40 m³)";
  else vehicle = "Container / multiple trips (40 m³+)";
  return { rows, totalVol, totalWeight, packages: mods.length, vehicle };
}

/** Module Schedule (~20644 repRenderSchedule). */
export function reportSchedule(state, lib) {
  const mods = reportModules(state, lib);
  const walls = (state && state.walls) || [];
  const wallName = (id) => {
    if (!id) return "Back wall";
    const idx = walls.findIndex((w) => w.id === id);
    return idx >= 0 ? "Wall " + (idx + 1) : "—";
  };
  const rows = mods.map(({ def, p }, i) => ({
    n: i + 1,
    code: def.code || "—",
    name: def.name || "",
    wall: wallName(p.wallId),
    pos: p.absLeft != null ? Math.round(p.absLeft * 1000) + " mm" : "—",
    dims: Math.round(def.width * 1000) + "×" + Math.round(def.depth * 1000) + "×" + Math.round(def.height * 1000),
    finish: (p.config && p.config.doorColor) ? p.config.doorColor : (p.config && p.config.carcass ? p.config.carcass : "—"),
  }));
  return { rows, count: mods.length };
}

/** Materials & Finishes summary (~20671 repRenderMaterials). */
export function reportMaterials(state, lib) {
  const mods = reportModules(state, lib);
  const cats = ["carcass", "door", "doorColor", "handle", "handleColor", "countertop"];
  const catLabels = { carcass: "Carcass", door: "Door style", doorColor: "Door colour", handle: "Handle", handleColor: "Handle colour", countertop: "Countertop" };
  const tally = {};
  mods.forEach(({ p }) => {
    const cfg = p.config || {};
    cats.forEach((cat) => {
      const val = cfg[cat];
      if (!val) return;
      if (!tally[cat]) tally[cat] = {};
      tally[cat][val] = (tally[cat][val] || 0) + 1;
    });
  });
  const rows = [];
  Object.keys(tally).forEach((cat) => {
    Object.entries(tally[cat]).forEach(([val, count]) => {
      rows.push({ category: catLabels[cat] || cat, value: val, count });
    });
  });
  return { rows, moduleCount: mods.length };
}

/* ── Drawings package data (dwpUnitRows ~54657 + dwpFmt ~54622) ───────────── */
export function drawingUnitRows(state, lib) {
  const g = {};
  ((state && state.placements) || []).forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    const key = def.code || p.type;
    if (!g[key]) g[key] = { def, qty: 0, price: 0 };
    g[key].qty++;
    g[key].price += def.price || 0;
  });
  return Object.values(g);
}

export function dwpFmt(val, units) {
  const m = parseFloat(val) || 0;
  if (units === "mm") return Math.round(m * 1000);
  if (units === "cm") return Math.round(m * 100);
  if (units === "m") return m.toFixed(2);
  return Math.round(m * 1000);
}

export function dwpFmtEUR(n) {
  return n > 0 ? "€" + Math.round(n).toLocaleString("de-DE") : "—";
}

export function dwpDateShort(d) {
  d = d || new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}
